/**
 * Experience letter formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const experienceLetterFormatsPack: ToilePack = {
    slug: 'experience-letter-formats',
    category: 'office',
    name: 'Experience letter formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'standard', label: 'Standard', subject: 'an experience letter format on letterhead with "To Whom It May Concern"'},
        {key: 'relieving', label: 'Relieving', subject: 'a relieving letter format'},
        {key: 'intern', label: 'Internship', subject: 'an internship certificate format'},
    ],
    styles: [
        {key: 'plain', label: 'Plain', style: 'plain formal certificate letter in a black serif, centered title, signature line at the lower right', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'seal', label: 'Seal', style: 'formal letter inside a fine ruled border with an empty circular stamp placeholder near the signature', palette: 'maroon and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'banded', label: 'Banded', style: 'company letter with a slim coloured band across the top and a light footer band', palette: 'teal and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['blue border', 'round stamp', 'with logo'],
    directionHint: 'try: blue border',
    seo: {
        title: 'Experience letter format: standard, relieving, internship',
        metaDescription: 'Nine printable experience letter formats: a standard, a relieving and an internship letter, each in a plain, seal or banded style.',
        h1: 'Experience letter format: standard, relieving, internship',
        tagline: 'Three experience letters, each in three styles, with the date and signature lines marked.',
        intro: 'An experience letter format is the certificate an employer writes for a past employee. These nine show a standard letter, a relieving letter that confirms a release from duties, and an internship certificate. Only the headings are lettered; grey lines hold the details. Type a twist, such as blue border, and it is a request.',
        useCases: [
            'proof of past work for a new job application',
            'a certificate at the end of an internship',
            'a relieving letter when someone leaves a company',
            'a template for an office to fill in each time',
        ],
        faq: [
            {q: 'What is the difference between an experience letter and a relieving letter?', a: 'An experience letter confirms the period and role someone worked. A relieving letter confirms that they have been released from their duties.'},
            {q: 'Is the signature included?', a: 'The date and signature lines are shown as blank lines to sign. Nothing is signed on the page.'},
            {q: 'What does the seal style show?', a: 'A fine ruled border and an empty round stamp placeholder near the signature. You add your own stamp.'},
            {q: 'Can I change the border colour?', a: 'Type it as a twist, such as blue border. It is a request for that colour, not a promised result.'},
        ],
    },
}
